import React, { useState, useEffect } from 'react';
import PageSEO from '../components/PageSEO';
import WebContactForm from '../components/WebContactForm';
import { SITE_INFO, BRAND_ASSETS } from '../data';
import { getTeamMembers } from '../lib/cmsClient';

export default function ContactPage() {
  const [contacts, setContacts] = useState(SITE_INFO.contacts);

  useEffect(() => {
    async function loadTeam() {
      try {
        const team = await getTeamMembers();
        if (team && team.length > 0) {
          setContacts(team);
        }
      } catch (err) {
        console.warn("Kunne ikke hente medarbejdere på kontaktsiden:", err);
      }
    }
    loadTeam();
  }, []);

  const contactGroups = [
    {
      title: 'Ledelse og administration',
      members: contacts.filter((c) => c.category === 'Ledelse og administration')
    },
    {
      title: 'Kloak og TV-inspektion',
      members: contacts.filter((c) => c.category === 'Kloak og TV-inspektion')
    },
    {
      title: 'Værksted',
      members: contacts.filter((c) => c.category === 'Værksted')
    }
  ];

  return (
    <div className="bg-stone-100 min-h-screen">
      <PageSEO
        title="Kontakt os | KTS-Entreprise A/S, Industrivej 3 Frederiksværk"
        description="Kontakt KTS-Entreprise, hvis du har spørgsmål eller ønsker et uforpligtende tilbud på entreprenør-, kloak-, jord- eller betonarbejde, materialer eller leje af køreplader."
        ogTitle="Kontakt KTS-Entreprise A/S"
        ogDescription="Hovedtelefon 47 77 25 77. Direkte kontaktpersoner i administration, kloak og værksted."
        ogImage={BRAND_ASSETS.defaultOgImage}
        slug="kontakt"
      />

      {/* Header Banner */}
      <section className="bg-stone-900 text-white pt-36 sm:pt-44 md:pt-48 lg:pt-52 pb-12 sm:pb-16 border-b-4 border-[#0072c0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0072c0]/20 border border-[#0072c0]/40 text-blue-200 text-xs font-heading font-bold uppercase tracking-wider mb-3 rounded-xs">
            <span>Kontakt</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white font-heading">
            Kontakt KTS-Entreprise
          </h1>
          <p className="text-stone-300 text-sm sm:text-base mt-2 max-w-2xl">
            Kontakt KTS-Entreprise, hvis du har spørgsmål eller ønsker et uforpligtende tilbud på entreprenør-, kloak-, jord- eller betonarbejde, materialer eller leje af køreplader.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        
        {/* Company Info Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          
          {/* Main Info */}
          <div className="lg:col-span-6 bg-white border border-stone-200 p-6 sm:p-8 rounded-xs shadow-xs">
            <div className="border-l-4 border-[#0072c0] pl-3.5 mb-6">
              <span className="text-xs font-bold uppercase tracking-wider text-[#0072c0] font-heading block">
                Hovedkontor &amp; Plads
              </span>
              <h2 className="text-2xl font-black uppercase tracking-tight text-stone-900 font-heading">
                KTS-Entreprise A/S
              </h2>
            </div>

            <div className="space-y-4 text-stone-800 text-sm sm:text-base">
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-location-dot text-[#0072c0] text-base mt-1 shrink-0" aria-hidden="true"></i>
                <div>
                  <span className="block font-bold text-stone-900">Industrivej 3</span>
                  <span className="text-stone-600">3300 Frederiksværk</span>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <i className="fa-solid fa-phone text-[#0072c0] text-base shrink-0" aria-hidden="true"></i>
                <div>
                  <span className="text-xs text-stone-500 uppercase block font-bold">Hovedtelefon</span>
                  <a
                    href={`tel:${SITE_INFO.mainPhone.replace(/\s+/g, '')}`}
                    className="font-bold text-xl font-mono text-stone-900 hover:text-[#0072c0] whitespace-nowrap transition-colors"
                  >
                    {SITE_INFO.mainPhone}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <i className="fa-solid fa-envelope text-[#0072c0] text-base shrink-0" aria-hidden="true"></i>
                <div>
                  <span className="text-xs text-stone-500 uppercase block font-bold">E-mail</span>
                  <a href={`mailto:${SITE_INFO.email}`} className="text-stone-900 hover:text-[#0072c0] font-medium">
                    {SITE_INFO.email}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <i className="fa-solid fa-id-card text-stone-500 text-base shrink-0" aria-hidden="true"></i>
                <div>
                  <span className="text-xs text-stone-500 uppercase block font-bold">CVR Nummer</span>
                  <span className="font-mono text-stone-900 font-bold">{SITE_INFO.cvr}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Opening hours & Map directions */}
          <div className="lg:col-span-6 bg-stone-900 text-white p-6 sm:p-8 rounded-xs border border-stone-800 flex flex-col justify-between">
            <div>
              <div className="border-l-4 border-[#0072c0] pl-3.5 mb-6">
                <span className="text-xs font-bold uppercase tracking-wider text-blue-300 font-heading block">
                  Åbningstider &amp; Plads
                </span>
                <h2 className="text-2xl font-black uppercase tracking-tight text-white font-heading">
                  Besøg pladsen i Frederiksværk
                </h2>
              </div>

              <div className="space-y-3 text-stone-300 text-xs sm:text-sm mb-6">
                <p>
                  <strong>Mandag–torsdag:</strong> kl. 6.30–15.30
                </p>
                <p>
                  <strong>Fredag:</strong> kl. 6.30–15.00
                </p>
                <p className="text-stone-400 text-xs pt-1">
                  Her modtager vi rene og sorterede bygge- og anlægsmaterialer og sælger genbrugsmaterialer, grus og sand. Privat salg foregår med kontant betaling eller MobilePay.
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-stone-800">
              <a
                href="https://maps.google.com/?q=Industrivej+3,+3300+Frederiksv%C3%A6rk"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#0072c0] hover:bg-[#005b99] text-white font-heading font-bold uppercase tracking-wider px-5 py-3 text-xs transition-colors rounded-xs w-full justify-center"
              >
                <i className="fa-solid fa-arrow-up-right-from-square text-xs"></i>
                <span>Åbn rutevejledning på Google Maps</span>
              </a>
            </div>
          </div>

        </div>

        {/* Direkte kontaktpersoner opdelt i grupper */}
        <section className="mb-16">
          <div className="border-l-4 border-[#0072c0] pl-4 mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-stone-900 font-heading">
              Direkte kontaktpersoner
            </h2>
            <p className="text-stone-600 text-sm mt-1">
              Find den rette kontaktperson inden for ledelse, kloak, administration og værksted.
            </p>
          </div>

          <div className="space-y-10">
            {contactGroups.map((group) => (
              <div key={group.title} className="space-y-4">
                <h3 className="text-lg font-heading font-bold uppercase text-stone-900 tracking-wider border-b border-stone-200 pb-2">
                  {group.title}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {group.members.map((person) => (
                    <div
                      key={person.name}
                      className="bg-white border border-stone-200 p-6 rounded-xs shadow-xs flex flex-col justify-between hover:border-[#0072c0]/40 transition-colors"
                    >
                      <div>
                        <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block mb-1.5">
                          {person.role}
                        </span>
                        <h4 className="font-heading font-bold text-lg sm:text-xl text-stone-900 uppercase tracking-tight">
                          {person.name}
                        </h4>
                      </div>

                      <div className="mt-6 pt-4 border-t border-stone-100 text-xs sm:text-sm space-y-2.5">
                        <a
                          href={`tel:${person.phone.replace(/\s+/g, '')}`}
                          className="flex items-center gap-2.5 text-stone-800 hover:text-[#0072c0] font-mono font-bold transition-colors"
                        >
                          <i className="fa-solid fa-phone text-[#0072c0]"></i>
                          <span>{person.phone}</span>
                        </a>
                        <a
                          href={`mailto:${person.email}`}
                          className="flex items-center gap-2.5 text-stone-600 hover:text-[#0072c0] transition-colors"
                        >
                          <i className="fa-solid fa-envelope text-[#0072c0]"></i>
                          <span>{person.email}</span>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Kontaktformular */}
        <div className="mt-16">
          <WebContactForm
            title="Send forespørgsel"
            subtitle="Udfyld formularen for et uforpligtende tilbud eller spørgsmål til vores ydelser."
            submitText="Send forespørgsel"
            showAddress={true}
            showTaskTypeSelect={true}
            taskTypeOptions={[
              "Entreprenør og anlæg",
              "Kloak og klimasikring",
              "Betonarbejde",
              { value: "Materialer og modtagelse", label: "Materialer på pladsen" },
              "Leje af køreplader",
              "Andet"
            ]}
            showFileUpload={true}
            fileLabel="Mulighed for at vedhæfte billeder eller tegninger (valgfrit)"
            showPrivacyLink={true}
          />
        </div>

      </div>
    </div>
  );
}
